import { ConfigurationWithMandatoryBuildabilityV2 } from '@volkswagen-onehub/car-configuration-feature-service';
import React, { useEffect, useState } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { dpsSend, VicciData } from 'src/bff';
import { getEquipmentlineName, getFeatures, getWheels } from 'src/bff/get-marketing-code';
import {
  FormController,
  removeWhiteSpace,
  useFaContent,
  useFeatureAppConfig,
  useGetInstalaciones,
  useGetWaidLitera,
  useOneFormContext,
  formatPrefix,
} from 'src/feature-app';
import { getParamsForm } from 'src/feature-app/get-params-form';
import {
  CarlineBase,
  FormDataGroup,
  FormDataHeaders,
  FormInfo,
  Horario,
  OneFormState,
  Steps,
} from 'src/types';
import { ThankYouLeads, getCodInterestCarline, getDescInterestCarline, getMatchData, getSalesGroup,  getOfertasData } from 'src/forms/Leads';
import { formatFields, formatFlags, formatHeaders, formDataBuilder, getDpsFields } from 'src/forms/format-fields';

export enum LeadGenerico {
  Empezar,
  Mapa,
  Modelo,
  NombreApellido,
  EmailTelefono,
  Contacto,
  Confirmacion,
}

export interface LeadGenericoFormProps {
  steps: Steps[];
  leadData: Partial<FormDataGroup>;
  leadInfo: Partial<FormInfo>;
  firstStep: Steps;
  lastStep: Steps;
}

export interface LeadsFormsProps {
  configRef?: ConfigurationWithMandatoryBuildabilityV2;
  updatedFormData?: Partial<FormDataGroup>;
  updatedFormInfo?: Partial<FormInfo>;
  isOfertasOrMatch?: boolean;
}

export const isOfertasForm = (formName: string) => formName === 'lead-oferta';
const getDescFormObjective = (formName: string, carline: CarlineBase, headers: FormDataHeaders) =>
  isOfertasForm(formName) && carline?.campaign?.dfo?.ofertas
    ? carline.campaign.dfo.ofertas
    : headers.DESC_FORMOBJECTIVE;

export function LeadInPageGenericoForm({ steps, leadData, leadInfo, firstStep, lastStep }: LeadGenericoFormProps) {
  const { initializeForm } = useOneFormContext();
  const store = useStore();
  const config = useFeatureAppConfig();
  const installations = useGetInstalaciones('VW_DDB_FRM_LEM_REC_LEM'); // Inicialización de las instalaciones
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const faContent = useFaContent();
  const waid = useGetWaidLitera();

  const getHorario = (horario: Horario) => {
    let horarioField = null;

    if (horario) {
      const horarioFormatted = horario === 'morning' ? 'MANANA' : horario === 'evening' ? 'MEDIODIA' : 'TARDE';

      horarioField = { 'fields[CITA_PREVIA_PREFERRED_SCHEDULE]': horarioFormatted };
    }
    return horarioField;
  };

  const getConfiguradorData = async () => {
    if (config.trigger.includes('configure')) {
      const { vicciData }: { vicciData: VicciData } = store.getState().formData.fields;

      return {
        'fields[COD_VEH_SALESGROUP]': vicciData?.consolidatedData?.vehicleConfiguration?.salesgroupKey,
        'fields[DESC_VEH_SALESGROUP]': vicciData?.consolidatedData?.vehicleConfiguration?.salesgroupName,
        'fields[DESC_TRIM_LINE]': getEquipmentlineName(vicciData?.consolidatedData?.vehicleConfiguration?.modelFilters),
        'fields[COD_VEH_TMAIMG]': vicciData?.consolidatedData?.vehicleConfiguration?.modelKey.split('$')[0],
        'fields[DESC_VEH_TMAIMG]': vicciData?.consolidatedData?.vehicleConfiguration?.modelName,
        'fields[DESC_VEH_MARKETING_CODE]': vicciData?.marketingCode,
        'fields[COD_VEH_COLOR]': vicciData?.consolidatedData?.vehicleConfiguration?.exterior?.code,
        'fields[DESC_VEH_COLOR]': vicciData?.consolidatedData?.vehicleConfiguration?.exterior?.name,
        'fields[COD_VEH_COLOR_INT]': vicciData?.consolidatedData?.vehicleConfiguration?.interior?.code,
        'fields[DESC_VEH_COLOR_INT]': vicciData?.consolidatedData?.vehicleConfiguration?.interior?.name,
        'fields[DESC_VEH_OFFER_PRICE]': vicciData?.priceData?.body?.amount
          ? `Precio final de la configuración: ${vicciData.priceData.body.amount}Eur`
          : undefined,
        'fields[COD_INTEREST_PR]': getFeatures(vicciData?.consolidatedData?.vehicleConfiguration?.features),
        ...getWheels(vicciData?.featuresData?.vehicleCatalogue?.features),
      };
    }
    return null;
  };

  const sendForm = (): Promise<boolean> => {
    const { formData, dealersInfo }: OneFormState = store.getState();
    const { fields, headers, flags } = formData;
    const { selectedInstallation, salesGroup, selectedCarline } = dealersInfo;

    const {
      TXT_V_Installation_Data_Name__c,
      TXT_V_KVPS_code__c,
      FRML_TXT_codVentaProvincia__c,
      TXT_V_Installation_Code__c,
    } = selectedInstallation;

    return new Promise<any>(async (resolve, reject) => {
      const { tc, today, referalUrl, url, device } = getParamsForm();
      const data = {
        ...formatHeaders(headers), // Headers comunes a todos los formularios de leads
        ...formatFields(fields.formFields), // Fields comunes a todos los formularios de leads
        ...formatFlags(flags), // Flags comunes a todos los formularios de leads
        'headers[COD_FORM_ID]': `vw-oneform-${fields.formName}`,
        'headers[DESC_URLORIGIN]': url,
        'headers[DESC_DEVICETYPE]': device,
        'headers[DESC_FORMOBJECTIVE]': getDescFormObjective(fields.formName, selectedCarline, headers),
        'headers[DESC_ID_CAMPAIGN]': selectedCarline?.campaign?.salesforce
          ? selectedCarline.campaign.salesforce
          : '7010Y0000001tAOQAY',

        'headers[FECHA_INSERTION_DATE]': today,
        'headers[TC]': tc,

        'fields[DESC_NAME]': fields.name,
        'fields[DESC_SURNAME_1]': fields.surname,
        'fields[DESC_SURNAME_2]': fields.secondSurname,
        'fields[DESC_EMAIL_ADDRESS]': removeWhiteSpace(fields.email),
        'fields[NUM_CELLPHONE]': fields.cellphone ? `${formatPrefix(fields.prefix)}${fields.cellphone}` : undefined,
        'fields[COD_LEAD_ID]': 'VW_DDB_FRM_LEM_REC_LEM',
        'fields[DESC_VEH_BRAND]': 'Volkswagen',
        'fields[DESC_FORM_VERSION]': `vw-oneform-${fields.formName}-${process.env.VERSION}`,
        'fields[DESC_DEALER_CODE_KVPS]': TXT_V_KVPS_code__c,
        'fields[DESC_DEALER_CODE]': TXT_V_Installation_Code__c,
        'fields[DESC_DEALER_PROVINCE]': fields.provinceName,
        'fields[COD_DEALER_PROVINCE]': FRML_TXT_codVentaProvincia__c,
        'fields[DESC_DEALER]': TXT_V_Installation_Data_Name__c,
        'fields[COD_INTEREST_CARLINE]': getCodInterestCarline(dealersInfo, fields.carlineCode),
        'fields[DESC_INTEREST_CARLINE]': getDescInterestCarline(dealersInfo, fields.modelo),
        'fields[DESC_PREFERRED_CONTACT_CHANNEL]': fields.contacto,
        'fields[DESC_LOPD]': 'PERMITE',
        'fields[DESC_TRANF_DATA_THIRD]': 'NO INFORMADO',
        'fields[DESC_LSSI]': fields.lssi ? fields.lssi : 'NO INFORMADO',
        'fields[DESC_LOPD_GDPR_PERFILADO]': fields.tmk ? fields.tmk : 'NO INFORMADO',
        'fields[DESC_DATA_ENRICHMENT]': 'NO INFORMADO',
        ...getSalesGroup(salesGroup),
        ...getHorario(fields.horario),
        ...getDpsFields(faContent),
        ...(await getOfertasData(formData, dealersInfo)),
        ...(await getMatchData(formData, dealersInfo, faContent)),
        ...(await getConfiguradorData()),
      };
      const dataStringified = JSON.stringify(data);
      const dataParsed = JSON.parse(dataStringified);

      const formDataInfo = formDataBuilder(dataParsed);

      const response = await dpsSend(formDataInfo);

      if (response && response.data && response.data.content && response.data.content.code === '200') {
        dispatch({ type: 'SET_FORM_RESPONSE', payload: response.data });
        resolve(true);
      } else {
        resolve(false);
      }
    });
  };

  const leadGenericoData: Partial<FormDataGroup> = {
    headers: {
      DESC_BRAND: 'VW', // OK
      DESC_CAMPAIGNBLOCK: '9. VW LEM', // OK
      DESC_WAID_LITERA: waid ? waid : 'B09C01_LEM_WA1',
      DESC_TOKEN: 'VW_DDB_FRM_LEM_REC_LEM', // OK
      DESC_VN_VO: 'VN', // OK
      DESC_GDPR_ADAPTED: true, // OK
      ...leadData.headers,
    },
    flags: {
      FLAG_MAILRECEPCIONLEAD: 'true',
      FLAG_ISQUALIFIED: 'false',
      REQUIERE_CONTACTO_DEALER: 'false',
    },
    fields: { ...leadData.fields },
  };

  const leadGenericoInfo: Partial<FormInfo> = {
    formStarted: true,
    fullScreen: firstStep.fullScreen ? true : false,
    lastStep,
    sendFormCallback: sendForm,
    numberOfScreens: steps.length,
    screenIndex: firstStep.screenIndex,
    step: firstStep,
    steps,
    stepsHistory: [steps[0].screenIndex],
    thankYouComponent: <ThankYouLeads />,
    ...leadInfo,
  };

  useEffect(() => {
    initializeForm(leadGenericoInfo, leadGenericoData);
    setLoading(false);
  }, []);

  return loading ? null : <FormController steps={steps} screenType="full-screen" />;
}
